<template>
  <section class="custom-app-pricing">
    <div class="row align-items-center">
      <div class="col">
        <h4 class="mb-1">ADAS Map Mobile pricing</h4>
        <p class="text-muted mb-0">
          One price for every user. Changing a price keeps the old one on record: it's deactivated with who replaced it and when, never edited or
          deleted.
        </p>
      </div>
      <div class="col-auto">
        <router-link to="/app-usage" class="btn btn-sm btn-outline-secondary me-2">Usage &amp; credits</router-link>
        <router-link to="/access-requests" class="btn btn-sm btn-outline-secondary">Access requests</router-link>
      </div>
    </div>
    <div class="alert alert-info mt-3 mb-0" role="note">
      These prices are charged. A function's price applies from its next use, and a markup change reaches ADAS IQ answers within about 5 minutes.
      Every charge records the price it was made at, so changing a price never changes past charges.
    </div>
    <div v-if="error" class="alert alert-danger mt-3 mb-0" role="alert">{{ error }}</div>
    <div v-if="saved" class="alert alert-success mt-3 mb-0" role="status">{{ saved }}</div>

    <div v-show="loading.settings && !settings.current.length" class="custom-spinner">
      <FontAwesomeIcon icon="fa-solid fa-spinner" spin />
    </div>

    <!-- Prices -->
    <h5 class="mt-4">Prices</h5>
    <table class="table table-bordered align-middle">
      <thead>
        <tr>
          <th>Setting</th>
          <th class="custom-column-value">Current</th>
          <th>Since</th>
          <th>Set by</th>
          <th class="custom-column-actions"></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="setting of definitions" :key="setting.code">
          <tr :data-test="`price-row-${setting.code}`">
            <td>
              <div class="fw-semibold">
                {{ setting.name }}
                <span
                  v-if="setting.info"
                  class="text-primary ms-1 custom-info"
                  tabindex="0"
                  role="img"
                  :title="setting.info"
                  :aria-label="setting.info"
                  :data-test="`info-${setting.code}`"
                >
                  <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
                </span>
              </div>
              <div class="text-muted small">{{ setting.help }}</div>
            </td>
            <td class="custom-column-value" data-test="price-value">{{ display(setting, valueOf(setting.code)) }}</td>
            <td>{{ formatDate(currentOf(setting.code)?.effectiveOn) }}</td>
            <td>{{ currentOf(setting.code)?.createdByName }}</td>
            <td class="custom-column-actions text-center">
              <button
                v-if="editing !== setting.code"
                class="btn btn-sm btn-outline-primary"
                type="button"
                :aria-label="`Change ${setting.name}`"
                @click="startEdit(setting)"
              >
                Change
              </button>
            </td>
          </tr>
          <tr v-if="editing === setting.code" class="custom-edit-row">
            <td colspan="5">
              <form class="row g-2 align-items-end" @submit.prevent="save(setting)">
                <div class="col-auto">
                  <label class="form-label small mb-1" :for="`value-${setting.code}`">New value ({{ setting.unitLabel }})</label>
                  <select
                    v-if="setting.kind === 'flag'"
                    :id="`value-${setting.code}`"
                    v-model.number="draft.value"
                    class="form-select form-select-sm"
                  >
                    <option :value="0">{{ flagLabels(setting)[0] }}</option>
                    <option :value="1">{{ flagLabels(setting)[1] }}</option>
                  </select>
                  <div v-else class="input-group input-group-sm">
                    <span v-if="setting.kind === 'usd'" class="input-group-text">$</span>
                    <input
                      :id="`value-${setting.code}`"
                      v-model.number="draft.value"
                      type="number"
                      :disabled="setting.noLimit && draft.value === 0"
                      class="form-control"
                      :min="setting.min"
                      :max="setting.max"
                      :step="setting.whole ? 1 : 0.01"
                      required
                    />
                    <span v-if="setting.kind === 'ratio'" class="input-group-text">: 1</span>
                    <span v-if="setting.kind === 'days'" class="input-group-text">days</span>
                    <span v-if="setting.kind === 'credits'" class="input-group-text">credits</span>
                  </div>
                </div>
                <div v-if="setting.noLimit" class="col-auto">
                  <div class="form-check mb-1">
                    <input
                      :id="`no-limit-${setting.code}`"
                      class="form-check-input"
                      type="checkbox"
                      :checked="draft.value === 0"
                      @change="draft.value = $event.target.checked ? 0 : null"
                    />
                    <label class="form-check-label small" :for="`no-limit-${setting.code}`">No limit</label>
                  </div>
                </div>
                <div class="col">
                  <label class="form-label small mb-1" :for="`note-${setting.code}`">Why is it changing?</label>
                  <input
                    :id="`note-${setting.code}`"
                    v-model="draft.note"
                    type="text"
                    class="form-control form-control-sm"
                    maxlength="500"
                    required
                  />
                </div>
                <div class="col-auto">
                  <button class="btn btn-sm btn-primary" type="submit" :disabled="loading.saving">Save new {{ setting.short }}</button>
                  <button class="btn btn-sm btn-link" type="button" @click="cancelEdit">Cancel</button>
                </div>
                <div v-if="draftError" class="col-12 text-danger small" role="alert">{{ draftError }}</div>
              </form>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <!-- What each action costs, from the current prices -->
    <h5 class="mt-4">What each action costs</h5>
    <table class="table table-bordered table-sm" data-test="scenarios">
      <thead>
        <tr>
          <th>User action</th>
          <th>AI usage</th>
          <th>Per-use charge</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row of scenarios" :key="row.action">
          <td>
            {{ row.action }}
            <span v-if="row.info" class="text-primary ms-1 custom-info" tabindex="0" role="img" :title="row.info" :aria-label="row.info">
              <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
            </span>
          </td>
          <td>{{ row.ai }}</td>
          <td>{{ row.charge }}</td>
        </tr>
      </tbody>
    </table>
    <p class="text-muted small">
      Repeating a history search or system lookup on the same VIN within {{ relookupDays }} days is free for that user; every image inspection is
      charged. Opening an inspection, its RO lines or extra information from a history result is part of the search. A lookup that fails is never
      charged. A subscription user whose balance can't cover a use gets a top-off automatically; an account on hold gets none.
    </p>

    <!-- History -->
    <h5 class="mt-4">Change history</h5>
    <table class="table table-bordered table-striped table-sm" data-test="price-history">
      <thead>
        <tr>
          <th>Setting</th>
          <th>Value</th>
          <th>Saved</th>
          <th>By</th>
          <th>Note</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row of settings.history" :key="row.id">
          <td>{{ nameOf(row.settingCode) }}</td>
          <td>{{ display(definitionOf(row.settingCode), Number(row.value)) }}</td>
          <td>{{ formatDate(row.createdOn) }}</td>
          <td>{{ row.createdByName }}</td>
          <td>{{ row.note }}</td>
          <td>
            <span v-if="row.isActive" class="badge bg-success">Active</span>
            <span v-else class="text-muted small">Replaced {{ formatDate(row.deactivatedOn) }} by {{ row.deactivatedByName }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { appPricingStore } from '@/stores/appPricing'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import moment from 'moment'

// The settings, in the order the page shows them. Ranges match the API (AppPricingController).
const DEFINITIONS = [
  {
    code: 'credit-price',
    name: 'Credit price',
    short: 'price',
    kind: 'usd',
    unitLabel: 'USD per credit',
    min: 0.01,
    max: 100,
    whole: false,
    help: "What one credit costs the customer. Every charge is in credits: a $5 lookup at $0.50 a credit uses 10. An unlimited account's uses are billed at this price a credit."
  },
  {
    code: 'plan-price',
    name: 'Subscription plan price',
    short: 'price',
    kind: 'usd',
    unitLabel: 'USD per subscription cycle',
    min: 0,
    max: 10000,
    whole: false,
    help: 'Billed per subscription cycle: on sign-up and on every billing anniversary, used or not. Unused plan credits expire at the anniversary.'
  },
  {
    code: 'plan-credits',
    name: 'Subscription plan credits',
    short: 'credits',
    kind: 'credits',
    unitLabel: 'credits per cycle',
    min: 1,
    max: 100000,
    whole: false,
    help: 'Credits a subscription gets on sign-up and on each billing anniversary. Set with the plan price: 60 credits for $29.95 to start.'
  },
  {
    code: 'top-up-price',
    name: 'Top-off price',
    short: 'price',
    kind: 'usd',
    unitLabel: 'USD per top-off',
    min: 0.01,
    max: 10000,
    whole: false,
    help: 'Billed per top-off (granted automatically when a use needs more than the balance). Top-off credits carry over.'
  },
  {
    code: 'top-up-credits',
    name: 'Top-off credits',
    short: 'credits',
    kind: 'credits',
    unitLabel: 'credits per top-off',
    min: 1,
    max: 100000,
    whole: false,
    help: 'Credits one top-off grants. Set with the top-off price: 30 credits for $15 to start.'
  },
  {
    code: 'adas-iq-markup',
    name: 'ADAS IQ (AI usage)',
    short: 'ratio',
    kind: 'ratio',
    unitLabel: 'customer price : our AI cost',
    min: 1,
    max: 100,
    whole: false,
    help: 'The customer pays this many times what the AI costs us for each question.'
  },
  {
    code: 'history-search-price',
    name: 'ADAS Service History Search',
    short: 'price',
    kind: 'usd',
    unitLabel: 'USD per search',
    min: 0,
    max: 1000,
    whole: false,
    help: 'Per VIN searched in the app.',
    info: "No charge when every result belongs to the user's own firm or a location under it. A history lookup made inside ADAS IQ is still charged this price, plus its AI usage."
  },
  {
    code: 'identify-adas-price',
    name: 'Identify ADAS Systems',
    short: 'price',
    kind: 'usd',
    unitLabel: 'USD per lookup',
    min: 0,
    max: 1000,
    whole: false,
    help: 'Per VIN looked up in the app.',
    info: "System lookups made inside ADAS IQ aren't charged this price; they're billed as AI usage only."
  },
  {
    code: 'image-inspection-price',
    name: 'Images Based ADAS Identification',
    short: 'price',
    kind: 'usd',
    unitLabel: 'USD per inspection',
    min: 0,
    max: 1000,
    whole: false,
    help: 'Per inspection created from photos.'
  },
  {
    code: 'free-relookup-days',
    name: 'Free re-lookup window',
    short: 'window',
    kind: 'days',
    unitLabel: 'days',
    min: 0,
    max: 365,
    whole: true,
    help: "The same user repeating a history search or system lookup on the same VIN within this many days isn't charged again (image inspections are always charged). 0 = every lookup is charged."
  },
  {
    code: 'charge-empty-results',
    name: 'Lookups that find nothing',
    short: 'setting',
    kind: 'flag',
    unitLabel: 'free or charged',
    min: 0,
    max: 1,
    whole: true,
    help: 'A history search or system lookup that comes back empty.'
  },
  // The daily guards (ADR-0016, split 2026-10-15): App usage and ADAS IQ usage, each on its own, both billing modes.
  // 'daily-usage-max' is the original single maximum, now App usage only.
  {
    code: 'daily-usage-max',
    name: 'Daily maximum App usage',
    short: 'maximum',
    kind: 'usd',
    unitLabel: 'USD per day',
    min: 0,
    max: 100000,
    whole: false,
    help: "The most one user's app functions (History Search, Identify ADAS Systems, images) may come to in a day (UTC), in dollars of credits, in either billing mode. Past it those functions are refused and the user is only told to contact OPUS IVS; ADAS IQ keeps working. Users are never shown the amount, that it is daily, or when it resets. Choose No limit to never refuse them.",
    noLimit: true,
    info: "ADAS IQ questions don't count toward this maximum; they have their own."
  },
  {
    code: 'daily-adas-iq-usage-max',
    name: 'Daily maximum ADAS IQ usage',
    short: 'maximum',
    kind: 'usd',
    unitLabel: 'USD per day',
    min: 0,
    max: 100000,
    whole: false,
    help: "The most one user's ADAS IQ questions may come to in a day (UTC), in dollars of credits, in either billing mode. Past it ADAS IQ is refused and the user is only told to contact OPUS IVS; the app's functions keep working. A guard against abuse of the AI: set it far above normal use. Users are never shown the amount, that it is daily, or when it resets. Choose No limit to never refuse them.",
    noLimit: true,
    info: "The app's functions don't count toward this maximum; they have their own. To cap only ADAS IQ, set the App usage maximum to No limit."
  }
]

export default {
  name: 'AppPricing',
  components: {
    FontAwesomeIcon
  },
  data() {
    return {
      definitions: DEFINITIONS,
      editing: null,
      draft: { value: null, note: '' },
      draftError: null,
      error: null,
      saved: null
    }
  },
  computed: {
    ...mapState(appPricingStore, ['settings', 'loading']),
    relookupDays() {
      return this.valueOf('free-relookup-days') ?? 0
    },
    scenarios() {
      const usd = (code) => this.display(this.definitionOf(code), this.valueOf(code))
      // A setting with no value yet (a new code on a tier that hasn't set it) reads as "not set", never as a blank.
      const shown = (code) => usd(code) || 'not set'
      const topOff = `${shown('top-up-credits')} for ${shown('top-up-price')}`
      const dailyMax = (code, what, other) =>
        this.valueOf(code) === 0
          ? `No limit: ${what} are never refused for the day's usage`
          : `${what} are refused for the rest of the day (UTC) once their total for the day would pass ${shown(
              code
            )}; ${other} keep working. The user is only told to contact OPUS IVS, never the amount`
      const markup = this.valueOf('adas-iq-markup')
      const ai = markup == null ? '' : `AI cost × ${this.trim(markup)}`
      const empty = this.valueOf('charge-empty-results') === 1
      return [
        { action: 'Asks ADAS IQ a question', ai, charge: '–' },
        { action: 'History Search in the app, results from other firms (or a mix)', ai: '–', charge: usd('history-search-price') },
        {
          action: "History Search in the app, every result the user's own firm's",
          ai: '–',
          charge: 'Free',
          info: this.definitionOf('history-search-price').info
        },
        {
          action: "ADAS IQ looks up a VIN's history",
          ai,
          charge: `${usd('history-search-price')}, even if every result is the user's own`,
          info: this.definitionOf('history-search-price').info
        },
        { action: 'Identify ADAS Systems in the app', ai: '–', charge: usd('identify-adas-price') },
        { action: "ADAS IQ looks up a vehicle's systems", ai, charge: 'None', info: this.definitionOf('identify-adas-price').info },
        { action: 'Images: an inspection is created', ai: '–', charge: usd('image-inspection-price') },
        { action: 'A history search or system lookup that finds nothing', ai: '–', charge: empty ? 'The usual price' : 'Free' },
        {
          action: 'A subscription starts, or reaches its billing anniversary',
          ai: '–',
          charge: `${shown('plan-price')} for ${shown('plan-credits')}, billed whether or not they were used; unused plan credits expire`
        },
        {
          action: 'A subscription user needs more than the balance holds',
          ai: '–',
          charge: `A top-off of ${topOff} is granted and billed automatically; top-off credits carry over`
        },
        {
          action: 'An unlimited account uses a function',
          ai: '–',
          charge: `Billed at ${shown('credit-price')} a credit, after the fact; no balance, no top-off`
        },
        {
          action: "A user's app functions pass the daily maximum App usage (either billing mode)",
          ai: '–',
          charge: dailyMax('daily-usage-max', 'App functions', 'ADAS IQ questions'),
          info: this.definitionOf('daily-usage-max').info
        },
        {
          action: "A user's ADAS IQ questions pass the daily maximum ADAS IQ usage (either billing mode)",
          ai: '–',
          charge: dailyMax('daily-adas-iq-usage-max', 'ADAS IQ questions', 'app functions'),
          info: this.definitionOf('daily-adas-iq-usage-max').info
        }
      ]
    }
  },
  async mounted() {
    await this.fetch()
  },
  methods: {
    ...mapActions(appPricingStore, ['getSettings', 'saveSetting']),
    async fetch() {
      try {
        await this.getSettings()
      } catch (error) {
        this.error = `Could not load pricing: ${error}`
      }
    },
    definitionOf(code) {
      return this.definitions.find((d) => d.code === code) || { code, name: code, kind: 'number' }
    },
    nameOf(code) {
      return this.definitionOf(code).name
    },
    currentOf(code) {
      return this.settings.current.find((c) => c.settingCode === code)
    },
    valueOf(code) {
      const current = this.currentOf(code)
      return current ? Number(current.value) : null
    },
    trim(value) {
      return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0$/, '')
    },
    display(setting, value) {
      if (value == null || Number.isNaN(value)) return ''
      // A daily maximum of 0 means no maximum: say so, never "$0.00".
      if (setting.noLimit && value === 0) return 'No limit'
      switch (setting.kind) {
        case 'usd':
          return `$${value.toFixed(2)}`
        case 'ratio':
          return `${this.trim(value)} : 1`
        case 'days':
          return `${value} days`
        case 'credits':
          return `${this.trim(value)} credits`
        case 'flag':
          return this.flagLabels(setting)[value === 1 ? 1 : 0]
        default:
          return String(value)
      }
    },
    flagLabels(setting) {
      return setting.options || ['Free', 'Charged']
    },
    formatDate(value) {
      return value ? moment(value).format('MM/DD/YYYY h:mm A') : ''
    },
    startEdit(setting) {
      this.editing = setting.code
      this.draft = { value: this.valueOf(setting.code), note: '' }
      this.draftError = null
      this.saved = null
    },
    cancelEdit() {
      this.editing = null
      this.draftError = null
    },
    async save(setting) {
      // Validate the typed text, not float arithmetic: `Math.round(v * 100) !== v * 100`
      // rejected valid cents (0.29, 0.57, 1.15, 4.35 ...) because v * 100 is not exact
      // in binary floating point, so an admin could not save those prices at all.
      const text = String(this.draft.value ?? '').trim()
      const value = Number(text)
      const upToTwoDecimals = /^(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(text)
      if (!upToTwoDecimals || Number.isNaN(value) || value < setting.min || value > setting.max || (setting.whole && !Number.isInteger(value))) {
        this.draftError = `Enter ${setting.whole ? 'a whole number' : 'a number with up to 2 decimals'} from ${setting.min} to ${setting.max}.`
        return
      }
      if (!this.draft.note || !this.draft.note.trim()) {
        this.draftError = 'Say why it is changing; the note stays with the price.'
        return
      }
      const before = this.valueOf(setting.code)
      try {
        const result = await this.saveSetting(setting.code, value, this.draft.note.trim())
        this.editing = null
        this.saved =
          result && result.changed
            ? `${setting.name} changed from ${this.display(setting, before)} to ${this.display(
                setting,
                value
              )}. The old value is kept in the change history.`
            : `${setting.name} is already ${this.display(setting, value)}; nothing changed.`
        await this.fetch()
      } catch (error) {
        this.draftError = `Could not save: ${error}`
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-spinner {
  text-align: center;
  padding: 2rem;
}
.custom-column-value {
  width: 12rem;
  font-variant-numeric: tabular-nums;
}
.custom-column-actions {
  width: 7rem;
}
.custom-info {
  cursor: help;
}
.custom-edit-row td {
  background: var(--bs-light);
}
</style>
